import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import api from '../api/api';
import './RestaurantNotificationSettings.css';

const SYSTEM_CHANGE_CONFIRMATION = 'CONFIRM SYSTEM CHANGE';

const modules = [
  { id: 'Dishes', label: 'Страви' },
  { id: 'SemiFinishedProducts', label: 'Напівфабрикати' },
  { id: 'EventOrders', label: 'Замовлення подій' },
];

type ChannelRoutes = Record<string, string[]>;
type Settings = {
  operatingGroupId: number;
  telegramEnabled: boolean;
  viberEnabled: boolean;
  telegramTokenConfigured: boolean;
  viberTokenConfigured: boolean;
  telegramTokenNeedsReentry: boolean;
  viberTokenNeedsReentry: boolean;
  telegramRecipients: ChannelRoutes;
  viberRecipients: ChannelRoutes;
};
type Restaurant = { id: number; name: string; isArchived: boolean };

const toTextRoutes = (routes: ChannelRoutes): Record<string, string> =>
  Object.fromEntries(modules.map(module => [module.id, (routes[module.id] || []).join('\n')]));
const toListRoutes = (routes: Record<string, string>): ChannelRoutes =>
  Object.fromEntries(modules.map(module => [
    module.id,
    (routes[module.id] || '').split(/\r?\n/).map(value => value.trim()).filter(Boolean),
  ]));
const errorMessage = (error: unknown) => axios.isAxiosError<{ message?: string }>(error)
  ? error.response?.data?.message || 'Не вдалося зберегти налаштування сповіщень.'
  : 'Не вдалося зберегти налаштування сповіщень.';

const RestaurantNotificationSettings: React.FC<{ canEdit: boolean }> = ({ canEdit }) => {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [serverEnabled, setServerEnabled] = useState(false);
  const [restaurantId, setRestaurantId] = useState('');
  const [settings, setSettings] = useState<Settings | null>(null);
  const [telegramRecipients, setTelegramRecipients] = useState<Record<string, string>>({});
  const [viberRecipients, setViberRecipients] = useState<Record<string, string>>({});
  const [telegramToken, setTelegramToken] = useState('');
  const [viberToken, setViberToken] = useState('');
  const [clearTelegramToken, setClearTelegramToken] = useState(false);
  const [clearViberToken, setClearViberToken] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const mountedRef = useRef(false);
  const requestGenerationRef = useRef(0);
  const selectedRestaurantIdRef = useRef('');
  const savingRef = useRef(false);
  const canEditRef = useRef(canEdit);
  canEditRef.current = canEdit;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestGenerationRef.current += 1;
    };
  }, []);

  useEffect(() => {
    if (canEdit) return;
    requestGenerationRef.current += 1;
    savingRef.current = false;
    setSaving(false);
    setTelegramToken('');
    setViberToken('');
    setClearTelegramToken(false);
    setClearViberToken(false);
    setError('');
    setMessage('');
  }, [canEdit]);

  useEffect(() => {
    const controller = new AbortController();
    api.get<{ serverEnabled: boolean; restaurants: Restaurant[] }>(
      '/notifications/settings/restaurants', { signal: controller.signal },
    ).then(({ data }) => {
      if (controller.signal.aborted) return;
      setRestaurants(data.restaurants);
      setServerEnabled(data.serverEnabled);
      setLoading(false);
    }).catch((reason: unknown) => {
      if (controller.signal.aborted) return;
      setError(errorMessage(reason));
      setLoading(false);
    });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!restaurantId) {
      setSettings(null);
      return;
    }
    const selectedRestaurantId = restaurantId;
    const requestGeneration = ++requestGenerationRef.current;
    const isCurrentRequest = () => mountedRef.current
      && requestGenerationRef.current === requestGeneration
      && selectedRestaurantIdRef.current === selectedRestaurantId;
    const controller = new AbortController();
    setSettings(null);
    setError('');
    setMessage('');
    setTelegramToken('');
    setViberToken('');
    setClearTelegramToken(false);
    setClearViberToken(false);
    api.get<Settings>(`/notifications/settings/${restaurantId}`, { signal: controller.signal })
      .then(({ data }) => {
        if (controller.signal.aborted || !isCurrentRequest()) return;
        setSettings(data);
        setTelegramRecipients(toTextRoutes(data.telegramRecipients));
        setViberRecipients(toTextRoutes(data.viberRecipients));
      }).catch((reason: unknown) => {
        if (!controller.signal.aborted && isCurrentRequest()) setError(errorMessage(reason));
      });
    return () => controller.abort();
  }, [restaurantId]);

  const save = async () => {
    if (!settings || !canEdit || savingRef.current || !restaurantId) return;
    const savedRestaurantId = restaurantId;
    const requestGeneration = requestGenerationRef.current;
    const restaurantName = restaurants.find(item => String(item.id) === savedRestaurantId)?.name || `#${savedRestaurantId}`;
    if (!window.confirm(`Підтвердити зміну Telegram/Viber для ресторану «${restaurantName}»?`)) return;
    if (requestGenerationRef.current !== requestGeneration
      || selectedRestaurantIdRef.current !== savedRestaurantId
      || !canEditRef.current) return;
    const isCurrentSave = () => mountedRef.current
      && requestGenerationRef.current === requestGeneration
      && selectedRestaurantIdRef.current === savedRestaurantId
      && canEditRef.current;
    savingRef.current = true;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const { data } = await api.put<Settings>(`/notifications/settings/${savedRestaurantId}`, {
        telegramEnabled: settings.telegramEnabled,
        viberEnabled: settings.viberEnabled,
        telegramRecipients: toListRoutes(telegramRecipients),
        viberRecipients: toListRoutes(viberRecipients),
        telegramBotToken: telegramToken.trim() || null,
        clearTelegramBotToken: clearTelegramToken,
        viberAuthToken: viberToken.trim() || null,
        clearViberAuthToken: clearViberToken,
        confirmation: SYSTEM_CHANGE_CONFIRMATION,
      });
      if (!isCurrentSave()) return;
      setSettings(data);
      setTelegramRecipients(toTextRoutes(data.telegramRecipients));
      setViberRecipients(toTextRoutes(data.viberRecipients));
      setTelegramToken('');
      setViberToken('');
      setClearTelegramToken(false);
      setClearViberToken(false);
      setMessage('Налаштування сповіщень збережено. Токени не повертаються з сервера.');
    } catch (reason: unknown) {
      if (isCurrentSave()) setError(errorMessage(reason));
    } finally {
      if (isCurrentSave()) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  };

  return <section className="restaurant-notification-settings" aria-label="Налаштування сповіщень ресторанів">
    <h3>Сповіщення ресторанів</h3>
    <p>Окремі маршрути для кожного ресторану. Глобальні чати й маршрути інших ресторанів не використовуються як резервні.</p>
    {error && <p role="alert">{error}</p>}
    {message && <p role="status">{message}</p>}
    <p>Токени Telegram і Viber спільні для маршрутів усіх ресторанів. Одержувачі та перемикачі каналів належать вибраному ресторану.</p>
    {loading && <p role="status">Завантаження ресторанів…</p>}
    {!loading && restaurants.length === 0 && !error && <p role="status">Ресторани не знайдено.</p>}
    {!loading && restaurants.length > 0 && <>
      {!serverEnabled && <p className="restaurant-notification-settings__warning" role="status">
        Сповіщення вимкнено на сервері. Збережені маршрути не надсилатимуть повідомлення, доки системний перемикач не буде ввімкнено окремо.
      </p>}
      <label className="restaurant-notification-settings__restaurant">
        Ресторан
        <select value={restaurantId} disabled={saving} onChange={event => {
          if (savingRef.current) return;
          const nextRestaurantId = event.target.value;
          requestGenerationRef.current += 1;
          selectedRestaurantIdRef.current = nextRestaurantId;
          setRestaurantId(nextRestaurantId);
        }}>
          <option value="">Оберіть ресторан</option>
          {restaurants.map(restaurant => <option key={restaurant.id} value={restaurant.id}>
            {restaurant.name}{restaurant.isArchived ? ' · архівний' : ''}
          </option>)}
        </select>
      </label>
      {!settings && restaurantId && !error && <p role="status">Завантаження налаштувань…</p>}
      {settings && <fieldset disabled={!canEdit || saving} className="restaurant-notification-settings__editor">
        <legend>Маршрути ресторану #{settings.operatingGroupId}</legend>
        <div className="restaurant-notification-settings__channels">
          <section aria-label="Telegram">
            <h4>Telegram</h4>
            <label className="restaurant-notification-settings__check">
              <input type="checkbox" checked={settings.telegramEnabled}
                onChange={event => setSettings({ ...settings, telegramEnabled: event.target.checked })} />
              Увімкнути Telegram-маршрути
            </label>
            <p>Токен: {settings.telegramTokenNeedsReentry
              ? 'потребує повторного введення'
              : settings.telegramTokenConfigured ? 'задано' : 'не задано'}.</p>
            {settings.telegramTokenNeedsReentry && <p className="restaurant-notification-settings__warning" role="alert">
              Збережений токен Telegram недоступний для розшифрування. Введіть його повторно для відновлення надсилання; збереження маршрутів не видаляє токен.
            </p>}
            <label>Замінити токен Telegram
              <input type="password" autoComplete="new-password" maxLength={4096} value={telegramToken}
                disabled={clearTelegramToken} placeholder={settings.telegramTokenConfigured ? 'Порожнє поле залишає токен без змін' : 'Вставте токен'}
                onChange={event => setTelegramToken(event.target.value)} />
            </label>
            <label className="restaurant-notification-settings__check">
              <input type="checkbox" checked={clearTelegramToken} onChange={event => {
                setClearTelegramToken(event.target.checked);
                if (event.target.checked) setTelegramToken('');
              }} />
              Видалити збережений токен Telegram
            </label>
            {modules.map(module => <label key={module.id}>{module.label} · одержувачі Telegram
              <textarea rows={3} maxLength={25700} value={telegramRecipients[module.id] || ''}
                placeholder="По одному chat ID в рядку"
                onChange={event => setTelegramRecipients({ ...telegramRecipients, [module.id]: event.target.value })} />
            </label>)}
          </section>
          <section aria-label="Viber">
            <h4>Viber</h4>
            <label className="restaurant-notification-settings__check">
              <input type="checkbox" checked={settings.viberEnabled}
                onChange={event => setSettings({ ...settings, viberEnabled: event.target.checked })} />
              Увімкнути Viber-маршрути
            </label>
            <p>Токен: {settings.viberTokenNeedsReentry
              ? 'потребує повторного введення'
              : settings.viberTokenConfigured ? 'задано' : 'не задано'}.</p>
            {settings.viberTokenNeedsReentry && <p className="restaurant-notification-settings__warning" role="alert">
              Збережений токен Viber недоступний для розшифрування. Введіть його повторно для відновлення надсилання; збереження маршрутів не видаляє токен.
            </p>}
            <label>Замінити токен Viber
              <input type="password" autoComplete="new-password" maxLength={4096} value={viberToken}
                disabled={clearViberToken} placeholder={settings.viberTokenConfigured ? 'Порожнє поле залишає токен без змін' : 'Вставте токен'}
                onChange={event => setViberToken(event.target.value)} />
            </label>
            <label className="restaurant-notification-settings__check">
              <input type="checkbox" checked={clearViberToken} onChange={event => {
                setClearViberToken(event.target.checked);
                if (event.target.checked) setViberToken('');
              }} />
              Видалити збережений токен Viber
            </label>
            {modules.map(module => <label key={module.id}>{module.label} · одержувачі Viber
              <textarea rows={3} maxLength={25700} value={viberRecipients[module.id] || ''}
                placeholder="По одному ID одержувача в рядку"
                onChange={event => setViberRecipients({ ...viberRecipients, [module.id]: event.target.value })} />
            </label>)}
          </section>
        </div>
        {!canEdit && <p>Перегляд без редагування. Зміни доступні системному адміністратору з правом редагування адмін-панелі.</p>}
        <button type="button" onClick={save} disabled={!canEdit || saving}>
          {saving ? 'Зберігаємо…' : 'Зберегти маршрути'}
        </button>
      </fieldset>}
    </>}
    <p>CRM показує лише наявність налаштувань, а не підтвердження доставки. Надсилання тестових повідомлень не передбачене.</p>
  </section>;
};

export default RestaurantNotificationSettings;
